/**
 * Plans & billing UI (paywall plans P3 + P4, redesign 2026-11, docs/DESIGN.md artboard 11): the Plans dialog (Free /
 * Cloud / Team / Lifetime, 7 free days on Cloud and Team through Checkout, € £ $, the comparison), Plan & billing in the
 * account menu (with usage meters), "Which worlds stay live", the desktop app's "Finish paying in your browser" wait,
 * and "Activating…" after Stripe sends someone back. Solo planning never shows any of it.
 */
import { Check, CircleAlert, Clock, ExternalLink, Infinity as InfinityIcon, Info, LoaderCircle, RefreshCw, Users, X } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useApp } from "../state/store.ts";
import { openSignIn, useAccount } from "../lib/auth.ts";
import { fmtBytes } from "../lib/collab.ts";
import type { MyWorld } from "../lib/collab.ts";
import { openExternal } from "../lib/desktop.ts";
import {
  cancelSubscription, changePlan, checkout, checkoutTrials, closePlans, currencies, daysLeft, fmtDate, fmtPrice, loadPlans, localCurrency, openLiveWorlds,
  openPlans, openPortal, ownedWorlds, priceIn, refreshBilling, setLiveWorlds, startTrialAndResume, trialDays, useBilling, type BillingStatus, type PaidPlan,
  type PlanPrice, type PlanRow,
} from "../lib/billing.ts";
import { AcademyLink } from "./Academy.tsx";
import { LegalLink } from "./Legal.tsx";
import "./plans.css";

const server = () => useApp.getState().collab.url;
const PAID: PaidPlan[] = ["cloud", "team", "lifetime"];
const PLAN_NAME: Record<PaidPlan, string> = { cloud: "Cloud", team: "Team", lifetime: "Lifetime" };
const SYMBOL: Record<string, string> = { eur: "€", gbp: "£", usd: "$" };
const gb = (n: number) => `${+(n / 1024 ** 3).toFixed(1)} GB`;
const Spin = ({ size = 16 }: { size?: number }) => <LoaderCircle size={size} className="spin" aria-hidden="true" />;

/**
 * A native modal <dialog> for as long as the component is mounted (docs: modern-web-guidance "dialog"): the page behind
 * is inert, focus stays inside, Esc closes (→ `onClose`). The first `[data-autofocus]` inside gets the focus.
 */
export function useModal(onClose: () => void) {
  const ref = useRef<HTMLDialogElement>(null);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (!d.open) {
      try { d.showModal(); } catch { d.setAttribute("open", ""); }
      d.querySelector<HTMLElement>("[data-autofocus]")?.focus();
    }
    // (a close event queued by StrictMode's unmount/remount finds the dialog open again: ignored)
    const onCloseEv = () => { if (!d.open) close.current(); };
    // Esc closes it here: the app's own key handlers (window) may otherwise take the key first and keep it open
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.isComposing) return;
      e.preventDefault();
      e.stopPropagation();
      close.current();
    };
    d.addEventListener("close", onCloseEv);
    d.addEventListener("keydown", onKey);
    return () => { d.removeEventListener("close", onCloseEv); d.removeEventListener("keydown", onKey); if (d.open) d.close(); };
  }, []);
  return ref;
}

/** clicks on the backdrop (outside the dialog's box) close it */
const outsideCloses = (onClose: () => void) => (e: React.MouseEvent<HTMLDialogElement>) => {
  if (e.target !== e.currentTarget) return;
  const r = e.currentTarget.getBoundingClientRect();
  if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) onClose();
};

/** "€0" (no cents for nothing) */
function zero(currency: string) {
  try { return new Intl.NumberFormat(navigator.language, { style: "currency", currency: currency.toUpperCase(), maximumFractionDigits: 0 }).format(0); } catch { return fmtPrice(0, currency); }
}

function priceText(price: PlanPrice | undefined, currency: string | null, withInterval = true) {
  if (!price) return "";
  const { amount, currency: c } = priceIn(price, currency);
  return `${fmtPrice(amount, c)}${withInterval ? (price.interval === "month" ? "\u00a0/\u00a0month" : "\u00a0once") : ""}`;
}

/** the comparison under the cards (limits from the database, so it can't drift from what the server enforces) */
function CompareTable({ plans, prices, currency, trials }: { plans: PlanRow[]; prices: PlanPrice[]; currency: string; trials: boolean }) {
  const cols = (["free", ...PAID] as string[]).map((id) => plans.find((p) => p.id === id)).filter((p): p is PlanRow => !!p);
  const yes = (b: boolean) => (b ? <Check size={14} className="yes" aria-label="Yes" /> : <span aria-label="No">–</span>);
  const rows: [string, (p: PlanRow) => ReactNode][] = [
    ["Price", (p) => (p.id === "free" ? zero(currency) : priceText(prices.find((x) => x.plan === p.id), currency) || "–")],
    ["Plan alone, every feature", () => yes(true)],
    ["Join friends' worlds", () => yes(true)],
    ["Host shared worlds", (p) => (p.id === "free" ? (trials ? "–" : "7-day trial (1)") : String(p.max_worlds))],
    ["People per world", (p) => (p.max_members ? String(p.max_members) : "–")],
    ["Cloud storage", (p) => (p.storage_bytes ? gb(p.storage_bytes) : "–")],
    ["Co-owners (admins)", (p) => yes(p.allow_admins)],
    ["Host on your own PC", (p) => (p.self_host ? "Soon" : "–")],
  ];
  if (trials) rows.splice(1, 0, ["Free days to start", (p) => { const d = trialDays(prices, p.id as PaidPlan); return d ? `${d} days` : "–"; }]);
  return (
    <div className="plans-compare" id="plans-compare">
      <table>
        <thead><tr><th><span className="sr-only">Plan</span></th>{cols.map((p) => <th key={p.id} scope="col">{p.name}</th>)}</tr></thead>
        <tbody>{rows.map(([label, f]) => <tr key={label}><th scope="row">{label}</th>{cols.map((p) => <td key={p.id} className="tabular">{f(p)}</td>)}</tr>)}</tbody>
      </table>
    </div>
  );
}

// ---------------- Plans dialog ----------------

export function PlansDialog() {
  const dialog = useBilling((s) => s.dialog);
  if (!dialog.open) return null;
  return <PlansDialogBody reason={dialog.reason} />;
}

const BLURB: Record<string, string> = {
  free: "Plan alone with every feature, forever.",
  cloud: "For you and a few friends.",
  team: "For servers and big groups.",
  lifetime: "Cloud for as long as MC Planner Cloud runs.",
};

function features(p: PlanRow): { text: ReactNode; soon?: boolean }[] {
  const worlds = `${p.max_worlds} shared world${p.max_worlds === 1 ? "" : "s"}`;
  if (p.id === "lifetime") return [{ text: "Everything in Cloud" }, { text: "Pay once" }, { text: <>Host on your own PC <span className="soon">Coming soon</span></>, soon: true }];
  const f: { text: ReactNode }[] = [{ text: worlds }, { text: `${p.max_members} people per world` }, { text: `${gb(p.storage_bytes)} cloud storage` }];
  f.push({ text: p.allow_admins ? "Co-owners who can invite" : "Editors and viewers" });
  return f;
}

function PlansDialogBody({ reason }: { reason?: string }) {
  const ref = useModal(closePlans);
  const { plans, prices, status, busy, error } = useBilling();
  const picked = useBilling((s) => s.currency);
  const signedIn = useAccount((s) => s.status === "signed-in");
  const worldName = useApp((s) => s.room?.name ?? s.level?.name ?? null);
  const [confirmDown, setConfirmDown] = useState(false);
  const [compare, setCompare] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(plans.length === 0);

  const load = () => {
    setLoadError(null);
    setLoading(true);
    void loadPlans(server()).catch((e) => setLoadError((e as Error).message)).finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    if (signedIn) void refreshBilling(server()).catch(() => null);
  }, [signedIn]);

  const cur = status?.plan;
  const sub = status?.subscription && ["active", "trialing", "past_due"].includes(status.subscription.status) ? status.subscription : null;
  const payments = prices.length > 0;
  const trials = checkoutTrials(prices);
  const currency = picked ?? (prices[0] ? localCurrency(prices[0]).currency : "eur");
  const currencyList = currencies(prices);
  /** may this person start this plan with its free days? (signed out: new accounts can) */
  const trialFor = (id: PaidPlan) => {
    if (!trials || !trialDays(prices, id)) return 0;
    if (!signedIn || !status) return trialDays(prices, id);
    if (!status.trial.available || sub || (id === "cloud" && status.lifetime)) return 0;
    return trialDays(prices, id);
  };
  const anyTrial = PAID.some((id) => trialFor(id) > 0);
  // the old card-free trial (servers before the Checkout trial)
  const oldTrialOffer = !trials && signedIn && status && status.trial.available && cur?.plan === "free";
  const featured: string = !cur || cur.plan === "free" || cur.plan === "trial" || cur.plan === "beta" ? "cloud" : cur.plan;

  const button = (id: PaidPlan) => {
    const look = id === featured ? "btn-primary" : "btn-outline";
    const b = (label: ReactNode, onClick: () => void, cls = look) => (
      <button type="button" className={`btn btn-lg btn-block ${cls}`} disabled={!!busy} onClick={onClick}>
        {busy === `checkout:${id}` || busy === `change:${id}` ? <><Spin />One moment…</> : label}
      </button>
    );
    const off = (label: string) => <button type="button" className="btn btn-lg btn-block btn-outline" disabled>{label}</button>;
    if (!signedIn) return b("Sign in to choose", () => openSignIn("Planning together needs a free account."));
    if (!payments) return off("Not available yet");
    if (!status) return <button type="button" className="btn btn-lg btn-block btn-outline" disabled><Spin />Loading…</button>;
    if (id === "lifetime") {
      if (status.lifetime) return off("You own Lifetime");
      return b("Buy Lifetime", () => void checkout(server(), "lifetime"));
    }
    if (sub?.plan === id) return off(sub.status === "trialing" ? "On your free days" : "Current plan");
    if (id === "cloud" && status.lifetime) return off("Included in Lifetime");
    if (sub && id === "team") return b("Switch to Team", () => void changePlan(server(), "team"));
    if (sub && id === "cloud") {
      return confirmDown
        ? (
          <div className="plan-confirm">
            <span className="caption">Switches now; the unused Team days become credit on your next bills.</span>
            <div className="plan-confirm-row">
              <button type="button" className="btn btn-md btn-outline" onClick={() => setConfirmDown(false)}>Keep Team</button>
              <button type="button" className="btn btn-md btn-primary" disabled={!!busy} onClick={() => { setConfirmDown(false); void changePlan(server(), "cloud"); }}>Switch to Cloud</button>
            </div>
          </div>
        )
        : b("Switch to Cloud", () => setConfirmDown(true), "btn-outline");
    }
    const days = trialFor(id);
    if (days) return b(`Start ${days} days free`, () => void checkout(server(), id, { trial: true }));
    return b(`Choose ${PLAN_NAME[id]}`, () => void checkout(server(), id));
  };

  const priceBlock = (id: PaidPlan) => {
    const price = prices.find((x) => x.plan === id);
    if (!price) return <div className="plan-price"><span className="plan-amount muted-amount">{loading ? <span className="skeleton" /> : "–"}</span></div>;
    const days = trialFor(id);
    const full = priceText(price, currency);
    if (days) {
      return (
        <div className="plan-price trial">
          <div className="plan-price-row"><span className="plan-amount free">Free</span><s className="tabular" aria-label={`instead of ${full}`}>{priceText(price, currency, false)}</s></div>
          <span className="plan-then tabular">{days} days free, then {full}</span>
        </div>
      );
    }
    return (
      <div className="plan-price">
        <div className="plan-price-row"><span className="plan-amount tabular">{priceText(price, currency, false)}</span><span className="plan-per">{price.interval === "month" ? "/ month" : "once"}</span></div>
        <span className="plan-then">{price.interval === "month" ? "Cancel any time" : "No subscription"}</span>
      </div>
    );
  };

  const lead = <>{worldName ? <>Go live in <b>{worldName}</b> and invite friends.</> : "Share a world and invite friends."} They join free with a free account.{anyTrial ? " Cloud and Team start with 7 days free: Stripe asks for your card, charges nothing until day 8, and cancelling before costs nothing." : ""}</>;

  return (
    <dialog ref={ref} className="plans-dialog mcp-acct" aria-labelledby="plans-title" onClick={outsideCloses(closePlans)}>
      <button type="button" className="btn btn-md btn-ghost btn-icon plans-close" onClick={closePlans} aria-label="Close" title="Close (Esc)"><X size={16} aria-hidden="true" /></button>
      <header className="plans-head">
        <span className="overline plans-over"><Users size={14} aria-hidden="true" />Plan together</span>
        <h2 id="plans-title" className="plans-title" tabIndex={-1} data-autofocus="">Planning alone is free, forever. Pay only to plan together.</h2>
        <p className="plans-lead">{lead} <AcademyLink to="plansCompared" about="the plans">Learn more</AcademyLink></p>
        {reason && <p className="plans-reason" role="status"><Info size={15} aria-hidden="true" />{reason}</p>}
        {cur && cur.plan !== "free" && (
          <p className="plans-current">You're on <b>{cur.name}</b>{sub?.status === "trialing" && sub.trial_end ? `, free until ${fmtDate(sub.trial_end)}` : cur.expires_at ? ` until ${fmtDate(cur.expires_at)}` : ""}.</p>
        )}
      </header>

      {oldTrialOffer && (
        <div className="trial-box">
          <div>
            <b>Try Cloud free for 7 days</b>
            <span className="caption">Share 1 world with up to 4 friends for 7 days. Then pick a plan, or the world pauses.</span>
          </div>
          <button type="button" className="btn btn-lg btn-primary" disabled={!!busy} onClick={() => void startTrialAndResume(server())}>
            {busy === "trial" ? <><Spin />Starting…</> : "Start free trial"}
          </button>
        </div>
      )}

      {loadError && !plans.length
        ? (
          <div className="plans-load-error" role="alert">
            <CircleAlert size={18} aria-hidden="true" />
            <span><b>The plans didn't load.</b> {loadError}</span>
            <button type="button" className="btn btn-md btn-outline" onClick={load}><RefreshCw size={14} aria-hidden="true" />Try again</button>
          </div>
        )
        : (
          <div className={`plan-cards${loading && !plans.length ? " loading" : ""}`} aria-busy={loading && !plans.length}>
            {(plans.length ? plans.filter((x) => x.id === "free" || PAID.includes(x.id as PaidPlan)) : (["free", ...PAID].map((id) => ({ id, name: id === "free" ? "Free" : PLAN_NAME[id as PaidPlan] }) as PlanRow)))
              .sort((a, b) => ["free", ...PAID].indexOf(a.id) - ["free", ...PAID].indexOf(b.id))
              .map((p) => {
                const id = p.id;
                const mine = id === "free" ? (!signedIn || !cur || cur.plan === "free") : (id === "lifetime" && !!status?.lifetime) || sub?.plan === id;
                const tag = mine
                  ? <span className="plan-tag">{id === "free" && !signedIn ? "Where everyone starts" : "Your plan"}</span>
                  : id === "cloud" && featured === "cloud" ? <span className="chip chip-accent plan-badge">For most groups</span>
                    : id === "lifetime" ? <InfinityIcon size={16} className="amber" aria-label="Pay once" /> : null;
                return (
                  <article key={id} className={`plan-card plan-${id}${id === featured ? " featured" : ""}${mine ? " mine" : ""}`} aria-labelledby={`plan-${id}-name`}>
                    <div className="plan-top"><span className="plan-name" id={`plan-${id}-name`}>{p.name}</span>{tag}</div>
                    {id === "free"
                      ? <div className="plan-price"><div className="plan-price-row"><span className="plan-amount tabular">{zero(currency)}</span></div><span className="plan-then">Forever</span></div>
                      : priceBlock(id as PaidPlan)}
                    <p className="plan-blurb">{BLURB[id]}</p>
                    {id === "free"
                      ? <button type="button" className="btn btn-lg btn-block btn-outline" disabled>{mine && signedIn ? "Current plan" : "Always free"}</button>
                      : plans.length ? button(id as PaidPlan) : <button type="button" className="btn btn-lg btn-block btn-outline" disabled><Spin />Loading…</button>}
                    <ul className="plan-feats">
                      {(id === "free"
                        ? [{ text: "Every planning tool" }, { text: "Desktop app and web" }, { text: "Join friends' worlds" }, ...(!trials && (!signedIn || !status || status.trial.available) ? [{ text: "Try Cloud free for 7 days" }] : [])]
                        : plans.length ? features(p) : []
                      ).map((f, i) => (
                        <li key={i} className={"soon" in f && f.soon ? "later" : ""}>{"soon" in f && f.soon ? <Clock size={15} aria-hidden="true" /> : <Check size={15} aria-hidden="true" />}<span>{f.text}</span></li>
                      ))}
                    </ul>
                  </article>
                );
              })}
          </div>
        )}

      {error && <p className="plans-error" role="alert"><CircleAlert size={15} aria-hidden="true" />{error}</p>}
      {compare && plans.length > 0 && <CompareTable plans={plans} prices={prices} currency={currency} trials={trials} />}

      <footer className="plans-foot">
        <span className="plans-promises">
          <span><Check size={14} aria-hidden="true" />Friends join free</span>
          <span><Check size={14} aria-hidden="true" />Cancel any time</span>
          <span><Check size={14} aria-hidden="true" />14-day refund on your first payment</span>
        </span>
        <span className="plans-tools">
          {currencyList.length > 1 && (
            <span className="seg" role="group" aria-label="Currency">
              {currencyList.map((c) => (
                <button key={c} type="button" aria-pressed={c === currency} onClick={() => useBilling.getState().set({ currency: c })}>{SYMBOL[c] ?? ""} {c.toUpperCase()}</button>
              ))}
            </span>
          )}
          <button type="button" className="plans-compare-toggle" aria-expanded={compare} aria-controls="plans-compare" onClick={() => setCompare(!compare)}>{compare ? "Hide comparison" : "Compare plans"}</button>
        </span>
        <span className="plans-fine">
          Prices include VAT. Payment runs through Stripe; your receipt comes from Link.{" "}
          <LegalLink path="legal/terms.html">Terms</LegalLink> · <LegalLink path="legal/refunds.html">Refunds</LegalLink>
        </span>
      </footer>
    </dialog>
  );
}

// ---------------- Plan & billing (account menu) ----------------

/** "then €4.99 / month": what the subscription costs after its free days (in its own currency) */
function afterTrial(st: BillingStatus, prices: PlanPrice[]) {
  const sub = st.subscription;
  const price = prices.find((p) => p.plan === sub?.plan);
  return price ? priceText(price, sub?.currency ?? null) : "";
}

function planLine(st: BillingStatus, prices: PlanPrice[]): { text: string; warn?: boolean } {
  const p = st.plan;
  const sub = st.subscription;
  if (p.source === "subscription" && sub) {
    const cancelled = sub.cancel_at_period_end || !!sub.cancel_at;
    if (sub.status === "past_due") return { text: `The last payment failed. Update your card by ${fmtDate(p.expires_at)} to keep planning together.`, warn: true };
    if (sub.status === "trialing" && sub.trial_end) {
      if (cancelled) return { text: `Free days cancelled: ends ${fmtDate(sub.trial_end)}. You won't be charged.` };
      const then = afterTrial(st, prices);
      return { text: `Free trial ends on ${fmtDate(sub.trial_end)}${then ? `, then ${then}` : ""}` };
    }
    if (cancelled) return { text: `Cancelled: ends ${fmtDate(p.expires_at)}` };
    return { text: `Renews ${fmtDate(p.renews_at)}` };
  }
  if (p.source === "trial") return { text: `Free trial: ${daysLeft(p.expires_at)} day${daysLeft(p.expires_at) === 1 ? "" : "s"} left` };
  if (p.source === "lifetime") return { text: "Lifetime: yours for good" };
  if (p.source === "beta") return { text: "Free for everyone while MC Planner is in beta" };
  if (p.source === "grant") return { text: p.expires_at ? `Given by the MC Planner team until ${fmtDate(p.expires_at)}` : "Given by the MC Planner team" };
  return { text: "Planning alone is free. Pick a plan to plan together." };
}

const SOURCE: Record<string, string> = { subscription: "Monthly", lifetime: "Paid once", trial: "Trial", beta: "Beta", grant: "Gift" };

export function BillingSection() {
  const status = useBilling((s) => s.status);
  const prices = useBilling((s) => s.prices);
  const busy = useBilling((s) => s.busy);
  const error = useBilling((s) => s.error);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);

  useEffect(() => {
    void refreshBilling(server()).then(() => setFailed(null)).catch((e) => setFailed((e as Error).message));
    if (!useBilling.getState().prices.length) void loadPlans(server()).catch(() => null);
  }, []);
  if (!status) {
    return (
      <div className="billing-section">
        <span className="overline">Plan &amp; billing</span>
        {failed
          ? <p className="bill-line err-text" role="alert">{failed} <button type="button" className="auth-link" onClick={() => void refreshBilling(server()).then(() => setFailed(null)).catch((e) => setFailed((e as Error).message))}>Try again</button></p>
          : <p className="bill-line muted"><Spin size={13} /> Loading your plan…</p>}
      </div>
    );
  }
  const p = status.plan;
  const line = planLine(status, prices);
  const sub = status.subscription && ["active", "trialing", "past_due"].includes(status.subscription.status) ? status.subscription : null;
  const cancelled = !!sub && (sub.cancel_at_period_end || !!sub.cancel_at);
  const trialing = sub?.status === "trialing";
  const source = trialing ? "Free days" : SOURCE[p.source];

  return (
    <div className="billing-section">
      <span className="overline">Plan &amp; billing</span>
      <div className="bill-plan"><b className="bill-name">{p.name}</b>{source && <span className={`chip${trialing || p.source === "trial" ? " chip-accent" : ""}`}>{source}</span>}</div>
      <p className={`bill-line${line.warn ? " warn-text" : ""}`}>{line.text}</p>
      {(p.max_worlds > 0 || status.usage.worlds > 0) && (
        <div className="meters">
          <UsageMeter label="Shared worlds" value={status.usage.worlds} max={p.max_worlds} text={`${status.usage.worlds} / ${p.max_worlds}`} />
          <UsageMeter label="Cloud storage" value={status.usage.storage_bytes} max={p.storage_bytes} text={`${fmtBytes(status.usage.storage_bytes)} / ${p.storage_bytes ? gb(p.storage_bytes) : "0"}`} />
          {p.max_worlds > 0 && <span className="caption">Up to {p.max_members} people per world</span>}
        </div>
      )}
      {status.usage.worlds > p.max_worlds && p.max_worlds > 0 && (
        <p className="bill-line warn-text">
          {status.usage.worlds - p.max_worlds} of your worlds {status.usage.worlds - p.max_worlds === 1 ? "is" : "are"} paused.{" "}
          <button type="button" className="auth-link" onClick={openLiveWorlds}>Choose which stay live</button>
        </p>
      )}
      <div className="bill-actions">
        <button type="button" className={`btn btn-sm ${p.plan === "free" ? "btn-primary" : "btn-outline"}`} onClick={() => openPlans()}>{p.plan === "free" || p.source === "trial" ? "See plans" : "Change plan"}</button>
        {status.customer && <button type="button" className="btn btn-sm btn-outline" disabled={!!busy} onClick={() => void openPortal(server())}>{busy === "portal" ? <><Spin size={14} />Opening…</> : <>Invoices &amp; card<ExternalLink size={13} aria-hidden="true" /></>}</button>}
        {sub && cancelled && <button type="button" className="btn btn-sm btn-soft" disabled={!!busy} onClick={() => void cancelSubscription(server(), true)}>{busy === "resume" ? <><Spin size={14} />Keeping it…</> : "Keep my subscription"}</button>}
      </div>
      {sub && !cancelled && (!confirmCancel
        ? <button type="button" className="auth-link is-danger bill-cancel" onClick={() => setConfirmCancel(true)}>{trialing ? "Cancel before you're charged…" : "Cancel subscription…"}</button>
        : (
          <div className="confirm-delete acct-confirm" role="alertdialog" aria-label="Cancel your subscription?">
            <p>
              {trialing
                ? <>Your free days go on until {fmtDate(sub.trial_end ?? sub.current_period_end)} and you won't be charged. After that your shared worlds pause (everyone keeps their plans and can export).</>
                : <>Your {p.name} plan stays until {fmtDate(sub.current_period_end)}. After that your shared worlds pause (everyone keeps their plans and can export).</>}
            </p>
            <div className="acct-confirm-row">
              <button type="button" className="btn btn-sm btn-outline" autoFocus onClick={() => setConfirmCancel(false)}>Keep it</button>
              <button type="button" className="btn btn-sm btn-danger" disabled={!!busy} onClick={async () => { await cancelSubscription(server()); setConfirmCancel(false); }}>{busy === "cancel" ? <><Spin size={14} />Cancelling…</> : "Cancel subscription"}</button>
            </div>
          </div>
        ))}
      {error && <p className="field-error" role="alert"><CircleAlert size={13} aria-hidden="true" />{error}</p>}
    </div>
  );
}

function UsageMeter({ label, value, max, text }: { label: string; value: number; max: number; text: string }) {
  const frac = max > 0 ? Math.min(1, value / max) : value > 0 ? 1 : 0;
  return (
    <div className="usage-meter" title={`${label}: ${text}`}>
      <div className="usage-row"><span className="caption">{label}</span><span className="caption tabular usage-val">{text}</span></div>
      <div className={`meter${value > max ? " full" : frac >= 0.85 ? " warn" : ""}`} role="meter" aria-label={label} aria-valuemin={0} aria-valuemax={max} aria-valuenow={value} aria-valuetext={text}>
        <i style={{ width: `${Math.round(frac * 100)}%` }} />
      </div>
    </div>
  );
}

// ---------------- which worlds stay live ----------------

export function LiveWorldsDialog() {
  const open = useBilling((s) => s.liveDialog);
  if (!open) return null;
  return <LiveWorldsBody />;
}

function LiveWorldsBody() {
  const close = () => useBilling.getState().set({ liveDialog: false });
  const ref = useModal(close);
  const status = useBilling((s) => s.status);
  const busy = useBilling((s) => s.busy);
  const error = useBilling((s) => s.error);
  const [worlds, setWorlds] = useState<MyWorld[] | null>(null);
  const [picked, setPicked] = useState<string[]>([]);
  const max = status?.plan.max_worlds ?? 0;

  useEffect(() => {
    void ownedWorlds(server()).then((w) => {
      setWorlds(w);
      setPicked(w.filter((x) => !x.paused).map((x) => x.room_id).slice(0, max));
    }).catch((e) => useBilling.getState().set({ error: (e as Error).message }));
  }, [max]);

  const toggle = (id: string) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : p.length < max ? [...p, id] : p));
  const save = async () => {
    const r = await setLiveWorlds(server(), picked);
    if (r) {
      useApp.getState().set({ notice: { text: picked.length === 1 ? "Saved: 1 world stays live." : `Saved: ${picked.length} worlds stay live.` } });
      close();
    }
  };

  return (
    <dialog ref={ref} className="acct-dialog live-worlds mcp-acct" aria-labelledby="live-title" onClick={outsideCloses(close)}>
      <div className="acct-dialog-head">
        <h2 className="title-sm" id="live-title" tabIndex={-1} data-autofocus="">Which worlds stay live?</h2>
        <button type="button" className="btn btn-sm btn-ghost btn-icon" onClick={close} title="Close (Esc)" aria-label="Close"><X size={16} aria-hidden="true" /></button>
      </div>
      <p className="acct-dialog-lead">
        Your {status?.plan.name ?? ""} plan keeps <b>{max}</b> shared world{max === 1 ? "" : "s"} live. The others pause: everyone can still open them, look around and
        export, and nothing is deleted. <AcademyLink to="liveWorlds" about="live and paused worlds">Learn more</AcademyLink>
      </p>
      {!worlds && !error && <p className="caption"><Spin size={13} /> Loading your worlds…</p>}
      {worlds && (
        <ul className="live-list">
          {worlds.map((w) => {
            const on = picked.includes(w.room_id);
            return (
              <li key={w.room_id}>
                <label className={on ? "on" : ""}>
                  <input type="checkbox" checked={on} disabled={!on && picked.length >= max} onChange={() => toggle(w.room_id)} />
                  <span className="live-name">{w.name}</span>
                  <span className="caption">{[w.storage_bytes ? fmtBytes(w.storage_bytes) : "", w.last_active_at ? `used ${fmtDate(w.last_active_at)}` : ""].filter(Boolean).join(" · ")}</span>
                </label>
              </li>
            );
          })}
        </ul>
      )}
      <p className="caption tabular live-count">{picked.length} of {max} picked</p>
      {error && <p className="field-error" role="alert"><CircleAlert size={13} aria-hidden="true" />{error}</p>}
      <div className="acct-dialog-actions">
        <button type="button" className="auth-link live-upgrade" onClick={() => { close(); openPlans("More worlds live at once: pick a bigger plan."); }}>Or pick a bigger plan</button>
        <button type="button" className="btn btn-md btn-ghost" onClick={close}>Cancel</button>
        <button type="button" className="btn btn-md btn-primary live-save" disabled={!worlds || !!busy} onClick={() => void save()}>{busy === "live" ? <><Spin />Saving…</> : "Save"}</button>
      </div>
    </dialog>
  );
}

// ---------------- desktop: paying in the browser ----------------

export function DesktopCheckoutDialog() {
  const waiting = useBilling((s) => s.waiting);
  if (!waiting) return null;
  return <DesktopCheckoutBody />;
}

function DesktopCheckoutBody() {
  const waiting = useBilling((s) => s.waiting)!;
  const close = () => useBilling.getState().set({ waiting: null });
  const ref = useModal(close);
  const name = PLAN_NAME[waiting.plan];
  return (
    <dialog ref={ref} className="acct-dialog billing-return mcp-acct" aria-label="Finish paying in your browser">
      <div className="acct-dialog-head"><h2 className="title-sm">Finish paying in your browser</h2></div>
      <p className="acct-dialog-lead">Stripe's checkout page is open in your web browser. <b>{name}</b> switches on here by itself as soon as you've paid.</p>
      <p className="caption wait-line"><Spin size={13} /> Waiting for the payment…</p>
      <div className="acct-dialog-actions">
        <button type="button" className="btn btn-md btn-ghost" onClick={close}>Cancel</button>
        <button type="button" className="btn btn-md btn-outline" data-autofocus="" onClick={() => void openExternal(waiting.url)}>Open the payment page again<ExternalLink size={14} aria-hidden="true" /></button>
      </div>
    </dialog>
  );
}

// ---------------- back from Stripe ----------------

export function BillingReturnDialog() {
  const activating = useBilling((s) => s.activating);
  if (!activating) return null;
  return <BillingReturnBody />;
}

function BillingReturnBody() {
  const activating = useBilling((s) => s.activating)!;
  const status = useBilling((s) => s.status);
  const prices = useBilling((s) => s.prices);
  const close = () => useBilling.getState().set({ activating: null });
  const ref = useModal(close);
  const name = activating.plan ? PLAN_NAME[activating.plan] : "your plan";
  const sub = status?.subscription;
  const trialing = activating.done && sub?.status === "trialing" && !!sub.trial_end;
  return (
    <dialog ref={ref} className="acct-dialog billing-return mcp-acct" aria-label="Payment" onClick={activating.done || activating.slow ? outsideCloses(close) : undefined}>
      {!activating.done && !activating.slow && (
        <>
          <div className="acct-dialog-head"><h2 className="title-sm">Activating {name}…</h2></div>
          <p className="acct-dialog-lead wait-line"><Spin size={14} /> Stripe is confirming it. This takes a few seconds.</p>
        </>
      )}
      {activating.done && (
        <>
          <div className="acct-dialog-head"><h2 className="title-sm"><span className="done-tick" aria-hidden="true"><Check size={14} strokeWidth={3} /></span>You're on {name}</h2></div>
          {trialing
            ? <p className="acct-dialog-lead">Free until {fmtDate(sub!.trial_end)}{afterTrial(status!, prices) ? `, then ${afterTrial(status!, prices)}` : ""}. Cancel any time before in Plan &amp; billing and you won't be charged.</p>
            : <p className="acct-dialog-lead">Open a world and use <b>Plan together</b> to share it and invite friends. Your receipt comes by email from Link.</p>}
          <div className="acct-dialog-actions"><button type="button" className="btn btn-md btn-primary" data-autofocus="" onClick={close}>Start planning together</button></div>
        </>
      )}
      {activating.slow && (
        <>
          <div className="acct-dialog-head"><h2 className="title-sm">Payment received</h2></div>
          <p className="acct-dialog-lead">It's taking longer than usual to switch {name} on. It happens by itself as soon as Stripe confirms it. You can keep planning meanwhile.{activating.emailing && " We'll email you as soon as it's on."}</p>
          <div className="acct-dialog-actions"><button type="button" className="btn btn-md btn-outline" data-autofocus="" onClick={close}>OK</button></div>
        </>
      )}
    </dialog>
  );
}
